import { useQuery } from '@tanstack/react-query';
import React, { useContext } from 'react';
import { getTopicFile } from 'src/apis/helpdesk/topic.api';
import { TopicContext } from 'src/contexts/topic.context';
import { useChatStore } from 'src/stores/chatStore';
import { IFilePath } from 'src/types/helpdesk/topic.type';
import './MessageImage.scss';
import { isBase64 } from 'src/utils/utils';

type Props = {
  file: IFilePath;
  isReplying?: boolean;
  className?: string;
  style?: React.CSSProperties;
  inView: boolean;
  isThumbnail?: boolean;
  isActive?: boolean;
  size?: number;
  topicId: string;
};
const MessageImage = ({ file, topicId, isReplying, className, style, inView, isThumbnail, isActive, size }: Props) => {
  const { openPreviewModal, setOpenPreviewModal } = useContext(TopicContext);
  const { files, setFile } = useChatStore();

  // ✅ Get URLs from store or fallback
  const thumbnailFileKey = `${topicId}-${file?.thumbnailPath}`;
  // const fullImageFileKey = `${topicId}-${file?.path}`;

  const cachedThumbnail = files[thumbnailFileKey];

  const thumbnailQuery = useQuery({
    queryKey: ['chatFileThumbnail', topicId, file?.thumbnailPath, file?.title, isThumbnail],
    queryFn: async () => {
      console.log(isBase64(file?.thumbnailPath), file?.thumbnailPath);
      const response = await getTopicFile(file?.thumbnailPath, file?.title);

      return response.data as Blob;
    },
    enabled:
      // inView &&
      !!file?.thumbnailPath && isThumbnail && !cachedThumbnail?.metadata?.thumbnail && !isBase64(file?.thumbnailPath),
    staleTime: 10 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    onSuccess: (blob) => {
      const blobUrl = URL.createObjectURL(blob);
      const fileKey = `${topicId}-${file?.thumbnailPath}`;

      setFile(fileKey, blobUrl, {
        roomId: topicId,
        fileId: file?.thumbnailPath,
        fileName: file?.title,
        fileType: blob.type,
        fileSize: blob.size,
        thumbnail: blobUrl
      });
    }
  });

  // ✅ Full image query - directly in component
  const fullImageQuery = useQuery({
    queryKey: ['chatFileFullImage', topicId, file?.path, file?.title, isThumbnail],
    queryFn: async () => {
      const response = await getTopicFile(file?.path, file?.title);
      return response.data as Blob;
    },
    enabled:
      // !!inView &&
      !isThumbnail && !!file?.path && file?.path !== file?.thumbnailPath && !isBase64(file?.path),
    staleTime: 30 * 60 * 1000,
    cacheTime: 2 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    onSuccess: (blob) => {
      const blobUrl = URL.createObjectURL(blob);
      const fileKey = `${topicId}-${file?.path}`;

      setFile(fileKey, blobUrl, {
        roomId: topicId,
        fileId: file?.path,
        fileName: file?.title,
        fileType: blob.type,
        fileSize: blob.size,
        fullImage: blobUrl
      });
    }
  });

  const imageUrl = cachedThumbnail?.url;

  // ✅ Check loading states
  const isLoading = thumbnailQuery.isLoading || (isActive && fullImageQuery.isLoading);

  const handleClick = () => {
    if (!openPreviewModal.isOpen && !isReplying && file?.path) {
      setOpenPreviewModal({ isOpen: true, selectedFile: file });
    }
  };

  // Not in view - show placeholder
  // if (!inView) {
  //   return (
  //     <div
  //       className='image-placeholder'
  //       style={{
  //         width: size || 120,
  //         height: size || 120,
  //         backgroundColor: '#f5f5f5',
  //         border: '1px dashed #d9d9d9',
  //         display: 'flex',
  //         alignItems: 'center',
  //         justifyContent: 'center',
  //         color: '#bdbdbd',
  //         borderRadius: 8,
  //         fontSize: 32,
  //         transition: 'all 0.2s'
  //       }}
  //     >
  //       <svg
  //         width='48'
  //         height='48'
  //         viewBox='0 0 24 24'
  //         fill='none'
  //         stroke='#bdbdbd'
  //         strokeWidth='2'
  //         strokeLinecap='round'
  //         strokeLinejoin='round'
  //       >
  //         <rect x='3' y='7' width='18' height='13' rx='2' />
  //         <circle cx='8.5' cy='13.5' r='2.5' />
  //         <polyline points='21 7 16.5 2.5 7.5 2.5 3 7' />
  //       </svg>
  //     </div>
  //   );
  // }

  // Loading state
  if (isLoading && !imageUrl) {
    return (
      <div
        className='image-loading'
        style={{
          width: size || 120,
          height: size || 120,
          backgroundColor: '#fafafa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 8,
          fontSize: 32,
          transition: 'all 0.2s'
        }}
      >
        <svg
          width='48'
          height='48'
          viewBox='0 0 24 24'
          fill='none'
          stroke='#90caf9'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <circle cx='12' cy='12' r='10' strokeDasharray='60' strokeDashoffset='40'>
            <animate attributeName='stroke-dashoffset' values='60;0' dur='1s' repeatCount='indefinite' />
          </circle>
        </svg>
      </div>
    );
  }

  // Nếu là file preview (local, đang xử lý)
  if (file.isPreview && (file.thumbnailPath || file.path)) {
    return (
      <div style={{ position: 'relative', display: 'inline-block', width: size || 120, height: size || 120 }}>
        <img
          src={file.thumbnailPath || file.path}
          alt={file.title}
          style={{
            width: size || 120,
            height: size || 120,
            objectFit: 'cover',
            borderRadius: 8,
            filter: 'blur(0.5px) grayscale(0.1)'
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0.6)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8
          }}
        >
          <span style={{ color: '#1976d2', fontWeight: 500, marginTop: 4, fontSize: 14 }}>Đang xử lý...</span>
        </div>
      </div>
    );
  }

  // Loaded image
  return (
    <>
      <img
        alt={file?.title}
        src={imageUrl}
        style={{
          ...style,
          width: isReplying && !size ? 50 : size || '100%',
          height: isReplying && !size ? 50 : size || '100%'
        }}
        loading='lazy'
        className={className}
        onClick={handleClick}
      />
      {/* {loadFullImage && <MessageImagePreview open={loadFullImage} setOpen={setLoadFullImage} file={file} />} */}
    </>
  );
};
export default MessageImage;
